import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import css from './views.module.css'
import { Card, Chip, Button, QueryError, MicroLabel, Notice, Disclosure } from '../ui'
import { useApprovals } from '../data/queries'
import { useDecide } from '../data/useDecide'
import {
  toQueueItem, readableKind, isOpen, verbsFor,
  CLOSE_STATE, DISMISS_STATE, REOPEN_STATE,
  type DecisionState,
} from '../data/map/approvals'
import { buildPrompt } from '../data/map/prompt'
import { HandOff } from './HandOff'
import { RunTrace } from './RunTrace'
import { count } from '../data/map/summaries'
import { age } from '../lib/time'

/**
 * Where the deciding happens once the queue says something needs a decision.
 *
 * The criteria are the point: the review agent checked each acceptance
 * criterion and wrote why it passed or failed, and this is the only place that
 * reasoning is readable. The queue carries the count; this carries the case.
 */
export function Detail() {
  const { id } = useParams<{ id: string }>()
  const approvals = useApprovals()
  const decide = useDecide()
  const navigate = useNavigate()
  const [note, setNote] = useState('')
  const [traceOpen, setTraceOpen] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const item = useMemo(() => {
    const row = (approvals.data ?? []).find((candidate) => candidate.id === id)
    return row ? toQueueItem(row) : null
  }, [approvals.data, id])

  if (approvals.error) return <QueryError error={approvals.error} />
  if (!approvals.data) return <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading…</div></Card>

  if (!item) {
    return (
      <Card>
        <div style={{ padding: 26 }}>
          <Link to="/inbox" className={css.backLink}>← Inbox</Link>
          <p style={{ fontSize: 14, color: 'var(--ink-2)' }}>
            No approval with that id. It may have been decided and dropped, or the link may be stale.
          </p>
        </div>
      </Card>
    )
  }

  const open = isOpen(item)
  const kind = readableKind(item.kind)
  const verbs = verbsFor(item)

  function submit(state: DecisionState) {
    if (!item) return
    setFailure(null)
    decide.mutate(
      { id: item.id, state, note: state === 'pending' ? null : note },
      {
        onError: (error) => setFailure(error.message),
        onSuccess: () => navigate('/inbox'),
      },
    )
  }

  return (
    <div className={css.detailGrid}>
      <div>
        <Link to="/inbox" className={css.backLink}>← Inbox</Link>

        <div className={css.qTags}>
          <Chip>{item.department}</Chip>
          {kind ? <Chip neutral>{kind}</Chip> : null}
          <span className={`${css.qSource} mono`}>{item.source}</span>
          <span className={css.qAge}>{item.age}</span>
        </div>

        <h2 className={css.detailTitle}>{item.title}</h2>
        {item.summary ? <p className={css.detailSummary}>{item.summary}</p> : null}

        {/* What is about to go out, and to whom. Approving this one sends it,
            so everything needed to say yes is here rather than in the rail. */}
        {item.distribution ? (
          <Card style={{ marginTop: 18 }}>
            <div className={css.section}>
              <MicroLabel>
                Ready to send
                {item.distribution.number !== null
                  ? ` · distribution ${item.distribution.number}${item.distribution.of !== null ? ` of ${item.distribution.of}` : ''}`
                  : ''}
              </MicroLabel>
            </div>
            <div className={css.section}>
              {item.distribution.artifactUrl ? (
                <a href={item.distribution.artifactUrl} target="_blank" rel="noreferrer" style={{ fontSize: 15, fontWeight: 600 }}>
                  Open the report →
                </a>
              ) : (
                <div className={css.criterionNote}>The fleet did not include a link to the report.</div>
              )}
            </div>
            <div className={css.section}>
              <div className={css.criterionText}>
                Goes to{item.distribution.channel ? ` by ${item.distribution.channel}` : ''}
              </div>
              {item.distribution.recipients.length > 0 ? (
                item.distribution.recipients.map((recipient) => (
                  <div key={recipient} className={`${css.criterionNote} mono`}>{recipient}</div>
                ))
              ) : (
                <div className={css.criterionNote}>No recipients listed.</div>
              )}
            </div>
            {item.distribution.dataAsOf || item.distribution.freshnessCheckedAt ? (
              <div className={css.section}>
                {item.distribution.dataAsOf ? (
                  <div className={css.criterionNote}>Data as of {stamp(item.distribution.dataAsOf)}</div>
                ) : null}
                {item.distribution.freshnessCheckedAt ? (
                  <div className={css.criterionNote}>Freshness checked {stamp(item.distribution.freshnessCheckedAt)}</div>
                ) : null}
              </div>
            ) : null}
            {item.distribution.number !== null && item.distribution.of !== null ? (
              <div className={css.section}>
                <div className={css.criterionNote}>
                  Each of the first {item.distribution.of} sends waits for a person. After that it goes
                  out on schedule without one.
                </div>
              </div>
            ) : null}
          </Card>
        ) : null}

        {/* In full and untruncated: this is the one part of a drafted spec that
            reads as prose, and the queue only had room for a line of it. */}
        {item.plain ? (
          <Card style={{ marginTop: 18 }}>
            <div className={css.section}>
              <MicroLabel>In plain terms</MicroLabel>
            </div>
            <div className={css.section}>
              <div className={css.criterionText} style={{ whiteSpace: 'pre-wrap' }}>{item.plain}</div>
            </div>
          </Card>
        ) : null}

        {item.blocking.length > 0 ? (
          <Card style={{ marginTop: 18 }}>
            <div className={css.section}>
              <MicroLabel>Blocking</MicroLabel>
            </div>
            {item.blocking.map((finding, index) => (
              <div key={index} className={css.section}>
                <div className={css.criterionText}>{finding.summary}</div>
                {finding.detail ? <div className={css.criterionNote}>{finding.detail}</div> : null}
                {finding.cites ? <div className={`${css.criterionNote} mono`}>cites {finding.cites}</div> : null}
              </div>
            ))}
          </Card>
        ) : null}

        {item.failure ? (
          <Card style={{ marginTop: 18 }}>
            <div className={css.section}>
              <MicroLabel>
                Why it stopped
                {item.failure.attemptsUsed !== null
                  ? ` · attempt ${item.failure.attemptsUsed}${item.failure.attemptCap !== null ? ` of ${item.failure.attemptCap}` : ''}`
                  : ''}
              </MicroLabel>
            </div>
            <div className={css.section}>
              <div className={`${css.criterionText} mono`} style={{ overflowWrap: 'anywhere' }}>{item.failure.error}</div>
              {item.failure.permanent === false ? (
                <div className={css.criterionNote}>
                  Not permanent — the same ticket could be run again once whatever caused this is
                  addressed.
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {item.assertions.length > 0 ? (
          <Card style={{ marginTop: 18 }}>
            <div className={css.section}>
              <MicroLabel>
                {item.assertionNoun === 'checks' ? 'Checks' : 'Acceptance criteria'} ·{' '}
                {item.assertions.filter((a) => a.ok === false).length > 0
                  ? `${item.assertions.filter((a) => a.ok === false).length} of ${item.assertions.length} failing`
                  : `${item.assertions.filter((a) => a.ok === true).length} of ${item.assertions.length} met`}
              </MicroLabel>
            </div>
            {/* A passing assertion that carries a reason is worth reading --
                that is the whole value of the review criteria. A passing one
                with nothing attached is a name and a tick, and fifteen of them
                bury the three that need attention. Split on what the row
                actually carries, not on which shape it came from. */}
            {item.assertions.filter((a) => a.ok !== true || a.note || a.samples.length > 0).map((assertion, index) => (
              <div key={index} className={css.section}>
                <div className={css.criterion}>
                  {/* Mono marks rather than colour alone: a failing check is
                      information, not an alarm. */}
                  <span
                    className={`${css.criterionMark} mono`}
                    style={{ color: assertion.ok === true ? 'var(--s-done)' : assertion.ok === false ? 'var(--s-fail)' : 'var(--ink-3)' }}
                  >
                    {assertion.ok === true ? '[x]' : assertion.ok === false ? '[!]' : '[?]'}
                  </span>
                  <div>
                    <div className={css.criterionText}>
                      {assertion.label}
                      {assertion.magnitude ? (
                        <span className={`${css.qMeta} mono`} style={{ marginLeft: 8 }}>{assertion.magnitude}</span>
                      ) : null}
                    </div>
                    {assertion.note ? <div className={css.criterionNote}>{assertion.note}</div> : null}
                    {assertion.samples.length > 0 ? (
                      <div style={{ marginTop: 6 }}>
                        {assertion.samples.map((line, sampleIndex) => (
                          <div key={sampleIndex} className={`${css.criterionNote} mono`} style={{ overflowWrap: 'anywhere' }}>
                            {line}
                          </div>
                        ))}
                        {assertion.sampleNote ? (
                          <div className={css.criterionNote} style={{ fontStyle: 'italic' }}>{assertion.sampleNote}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
            {(() => {
              const quiet = item.assertions.filter((a) => a.ok === true && !a.note && a.samples.length === 0)
              if (quiet.length === 0) return null
              return (
                <div className={css.section}>
                  <div className={css.criterion}>
                    <span className={`${css.criterionMark} mono`} style={{ color: 'var(--s-done)' }}>[x]</span>
                    <div>
                      <div className={css.criterionText}>
                        {quiet.length} {quiet.length === 1 ? 'check' : 'checks'} passed with nothing to report
                      </div>
                      <div className={`${css.criterionNote} mono`}>{quiet.map((a) => a.label).join(' · ')}</div>
                    </div>
                  </div>
                </div>
              )
            })()}
          </Card>
        ) : null}

        {item.proposals.map((group) => (
          <Card key={group.heading} style={{ marginTop: 18 }}>
            <div className={css.section}>
              <MicroLabel>{group.heading}</MicroLabel>
            </div>
            {group.items.map((entry, index) => (
              <div key={index} className={css.section}>
                <div className={css.criterion}>
                  {/* No pass mark: these were proposed, not evaluated. Showing
                      a tick would claim a result nobody produced. */}
                  <span className={`${css.criterionMark} mono`} style={{ color: 'var(--ink-3)' }}>–</span>
                  <div className={css.criterionText}>{entry}</div>
                </div>
              </div>
            ))}
          </Card>
        ))}

        {/* The two cards below are the working, not the case: what the agent
            verified and what the review noticed without blocking. Closed to a
            count, so the blocking findings and the criteria above are what the
            page reads as; one click opens either in full. */}
        {item.evidence.length > 0 ? (
          <Card style={{ marginTop: 18 }}>
            <Disclosure
              label
              title="Premises the agent checked rather than assumed"
              summary={count(item.evidence.length, 'premise')}
            >
              {item.evidence.map((entry, index) => (
                <div key={index} className={css.section}>
                  <div className={css.criterionText}>{entry.claim}</div>
                  {entry.found ? <div className={css.criterionNote}>found {entry.found}</div> : null}
                  {entry.checked ? (
                    <div className={`${css.criterionNote} mono`} style={{ overflowWrap: 'anywhere' }}>{entry.checked}</div>
                  ) : null}
                </div>
              ))}
            </Disclosure>
          </Card>
        ) : null}

        {item.comments.length > 0 ? (
          <Card style={{ marginTop: 18 }}>
            <Disclosure
              label
              title="Notes the review raised but did not block on"
              summary={count(item.comments.length, 'note')}
            >
              {item.comments.map((comment, index) => (
                <div key={index} className={css.section}>
                  <div className={css.criterion}>
                    <span className={`${css.criterionMark} mono`} style={{ color: 'var(--accent)' }}>?</span>
                    <div>
                      <div className={css.criterionText}>{comment.summary}</div>
                      {comment.detail ? <div className={css.criterionNote}>{comment.detail}</div> : null}
                      {comment.cites ? <div className={`${css.criterionNote} mono`}>cites {comment.cites}</div> : null}
                    </div>
                  </div>
                </div>
              ))}
            </Disclosure>
          </Card>
        ) : null}

        {item.assertions.length === 0 &&
        item.comments.length === 0 &&
        item.blocking.length === 0 &&
        item.proposals.length === 0 &&
        item.evidence.length === 0 &&
        !item.failure &&
        !item.distribution ? (
          <Notice>
            This item carries no results, findings or notes that this dashboard recognises — only
            its summary and the values in the rail. If it should show more, the shape it was
            written in is one no adapter reads yet.
          </Notice>
        ) : null}

        <HandOff prompt={buildPrompt(item)} />

        <div className={css.decisionBar}>
          {open ? (
            <>
              <input
                className={css.input}
                value={note}
                autoFocus={dismissing}
                placeholder={dismissing ? verbs.dismissPrompt : 'Short note for the record (optional)'}
                onChange={(event) => setNote(event.target.value)}
                aria-label={dismissing ? 'Reason for dismissing' : 'Note for the record'}
                style={{ flex: '1 1 220px' }}
              />
              {dismissing ? (
                <>
                  <Button variant="primary" disabled={decide.isPending} onClick={() => submit(DISMISS_STATE)}>
                    {verbs.dismiss}
                  </Button>
                  <Button variant="quiet" disabled={decide.isPending} onClick={() => setDismissing(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="primary" disabled={decide.isPending} onClick={() => submit(CLOSE_STATE)}>
                    {verbs.close}
                  </Button>
                  {/* Prompted, not required: dismissing is where the note stops
                      the same finding being raised again next week. */}
                  <Button variant="quiet" disabled={decide.isPending} onClick={() => setDismissing(true)}>
                    {verbs.dismiss}
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <span style={{ fontSize: 14, color: 'var(--ink-2)', flex: '1 1 auto' }}>
                {verbs.decided} · {item.decidedBy ?? 'you'} · {item.decidedAt ? age(item.decidedAt) : 'just now'}
                {item.decisionNote ? ` · ${item.decisionNote}` : ''}
              </span>
              <Button disabled={decide.isPending} onClick={() => submit(REOPEN_STATE)}>Reopen</Button>
            </>
          )}
        </div>
        {failure ? <div className={css.qError}>{failure}</div> : null}
      </div>

      <div className={css.rail}>
        <Card style={{ padding: '14px 18px' }}>
          <MicroLabel>Provenance</MicroLabel>
          <RailRow label="Verdict" value={item.verdict} />
          <RailRow label="Branch" value={item.buildBranch} mono />
          <RailRow label="Attempt" value={item.attempt === null ? null : String(item.attempt)} mono />
          <RailRow label="Ticket" value={item.ticketRef} mono />
          {/* Harvested from the context rather than named here, so a new
              shape's project, timestamp or commit appears without a change. */}
          {item.facts.map((fact) => (
            <RailRow key={fact.label} label={fact.label} value={fact.value} mono />
          ))}
          <RailRow label="Context" value={item.schemaVersion === null ? null : `schema ${item.schemaVersion}`} mono />
          <div style={{ marginTop: 10, fontSize: 12 }}>
            <button
              onClick={() => setTraceOpen(true)}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--brand-ink)', cursor: 'pointer' }}
            >
              Full run trace →
            </button>
          </div>
        </Card>

        {item.refUrl || item.ticketUrl ? (
          <Card style={{ padding: '14px 18px' }}>
            <MicroLabel>Source</MicroLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7, fontSize: 12 }}>
              {item.refUrl ? <a href={item.refUrl} target="_blank" rel="noreferrer">{verbs.refLink}</a> : null}
              {item.ticketUrl ? <a href={item.ticketUrl} target="_blank" rel="noreferrer">Issue in Linear</a> : null}
            </div>
            {/* A distribution is the one shape whose context says what approving
                does: it names the recipients and the channel. Everything else
                has not told us, so this does not guess. An effect line the
                dashboard invented would be a promise nothing here keeps. */}
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
              {item.distribution
                ? `${verbs.close} lets the fleet send this report to the recipients listed, usually within 15 minutes. ${verbs.dismiss} cancels this run; nothing is sent.`
                : 'Closing records that you dealt with this. It does not merge, deploy or authorise anything — the work itself happens in the source system.'}
            </div>
          </Card>
        ) : null}
      </div>

      <RunTrace item={traceOpen ? item : null} onClose={() => setTraceOpen(false)} />
    </div>
  )
}

/** "Oct 9, 2026, 3:12 AM · 4h ago" -- a timestamp the approver is judging the age of. */
function stamp(iso: string): string {
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return iso
  return `${t.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })} · ${age(iso)}`
}

function RailRow({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  if (!value) return null
  return (
    <div className={css.railRow}>
      <span className={css.railLabel}>{label}</span>
      <span className={mono ? `${css.railValue} mono` : css.railValue}>{value}</span>
    </div>
  )
}
